import { Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import { cn } from '~/utils/cn';

export type DeliveryWizardStepKey = 'decision' | 'preview' | 'progress';

const STEP_ORDER: { key: DeliveryWizardStepKey; order: 1 | 2 | 3 }[] = [
  { key: 'decision', order: 1 },
  { key: 'preview', order: 2 },
  { key: 'progress', order: 3 },
];

interface Props {
  step: DeliveryWizardStepKey;
  /** Step 2 is reachable straight from the stepper only once the operator has
   *  approved and picked at least one attribute — mirrors the shared footer's
   *  own Continue-enabled rule, so the two controls never disagree. */
  canContinueToPreview: boolean;
  /** Feeds the completed Decision step's "{n} attributes approved" hint. */
  approvedAttributeCount: number;
  /** Destination platform display label (e.g. "Google Ads"), for the
   *  Delivery step's hint — the platform varies per batch, unlike a
   *  single-destination mock. */
  destinationLabel: string;
  onGoTo: (step: 'decision' | 'preview') => void;
  /** Retry delivery: step 1 is "choose attributes" rather than approve / reject. */
  isRedelivery?: boolean;
}

/**
 * Compact 3-step row for the delivery wizard: circle + label + one-line hint
 * per step, joined by connectors that fill on completion. A wizard-specific
 * fork of the generic `Stepper` (`~/components/common/stepper.tsx`) rather
 * than an extension of it — mirrors the existing `experiment-stepper.tsx`
 * precedent for the same reason: this wizard's reachability rules (Preview
 * only reachable once approved with a selection; Progress never reachable
 * from here, only via "Confirm & deliver") and per-step hints are specific to
 * this domain and don't generalize to the shared component's other consumer.
 *
 * Progress is a one-way door: once delivery has started, neither earlier step
 * is clickable — nothing here can undo an in-flight or completed delivery.
 * Rendered `done` (green check) regardless, since it's still true that the
 * operator completed it; only the click-ability differs from a normal
 * completed step, so the "upcoming, unreachable" muted/disabled treatment is
 * reserved for genuinely-upcoming steps, never applied here.
 */
export function DeliveryWizardStepper({
  step,
  canContinueToPreview,
  approvedAttributeCount,
  destinationLabel,
  onGoTo,
  isRedelivery = false,
}: Props) {
  const { t } = useTranslation('common');
  const currentOrder = STEP_ORDER.find((s) => s.key === step)?.order ?? 1;

  // Three equal columns, one per step (each `<li>` holds its own connector +
  // button, so a 5-track `1fr auto 1fr auto 1fr` grid put Preview in an `auto`
  // track and squeezed the others). Equal fixed tracks keep the steps from
  // shifting when Decision's hint changes; hints wrap rather than truncate,
  // and the page gives this list enough width (`min-w-[min(100%,51rem)]` ≈
  // 17rem per step) that they fit on one line whenever it can.
  return (
    <ol
      aria-label={t('deliveryWizard.stepper.ariaLabel')}
      data-testid="delivery-wizard-stepper"
      className="flex flex-col gap-2 sm:grid sm:grid-cols-3 sm:items-start sm:gap-0"
    >
      {STEP_ORDER.map((s, i) => {
        const done = s.order < currentOrder;
        const current = s.order === currentOrder;
        const reachable =
          s.key === 'decision'
            ? step === 'preview'
            : s.key === 'preview'
              ? step === 'decision' && canContinueToPreview
              : false;
        const clickable = reachable && !current;
        const upcoming = !done && !current;

        return (
          <li
            key={s.key}
            className="contents sm:block sm:min-w-0"
            data-testid={`delivery-wizard-step-${s.key}`}
          >
            {i > 0 && (
              <span
                aria-hidden="true"
                className="hidden shrink-0 self-center px-1 sm:block sm:w-10 lg:w-16"
              >
                <span
                  className={cn(
                    'block h-0.5 origin-left rounded-full transition-transform duration-300 motion-reduce:transition-none',
                    done ? 'bg-success' : 'bg-border',
                  )}
                  style={{ transform: done ? 'scaleX(1)' : 'scaleX(0)' }}
                />
              </span>
            )}
            <Button
              type="button"
              variant="ghost"
              aria-current={current ? 'step' : undefined}
              data-testid={`delivery-wizard-step-${s.key}-button`}
              {...(upcoming && !clickable ? { disabled: true } : {})}
              onClick={() => {
                if (clickable && s.key !== 'progress') onGoTo(s.key);
              }}
              className={cn(
                'h-auto w-full items-start justify-start gap-3 rounded-xl p-3 text-left whitespace-normal',
                clickable
                  ? 'hover:bg-muted/40 cursor-pointer'
                  : 'cursor-default hover:bg-transparent',
              )}
            >
              {done ? (
                <span className="bg-success flex size-8 shrink-0 items-center justify-center rounded-full text-white">
                  <Check aria-hidden="true" className="size-4" />
                </span>
              ) : current ? (
                <span className="bg-primary ring-primary/20 flex size-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold text-white ring-4">
                  {s.order}
                </span>
              ) : (
                <span className="border-border text-muted-foreground flex size-8 shrink-0 items-center justify-center rounded-full border bg-white text-sm font-semibold">
                  {s.order}
                </span>
              )}
              <span className="min-w-0">
                <span
                  className={cn(
                    'block text-sm font-semibold',
                    current ? 'text-primary' : upcoming && 'text-muted-foreground',
                  )}
                >
                  {isRedelivery && s.key === 'decision'
                    ? t('deliveryWizard.stepper.redeliverDecision.label')
                    : t(`deliveryWizard.stepper.${s.key}.label`)}
                </span>
                <span className="text-muted-foreground block text-xs">
                  {done && s.key === 'decision' ? (
                    <>
                      {t('deliveryWizard.stepper.decision.approvedHint', {
                        count: approvedAttributeCount,
                      })}{' '}
                      <span className="text-primary font-medium">
                        {t('deliveryWizard.stepper.edit')}
                      </span>
                    </>
                  ) : isRedelivery && s.key === 'decision' ? (
                    t('deliveryWizard.stepper.redeliverDecision.hint')
                  ) : s.key === 'progress' ? (
                    t('deliveryWizard.stepper.progress.hint', { destination: destinationLabel })
                  ) : (
                    t(`deliveryWizard.stepper.${s.key}.hint`)
                  )}
                </span>
              </span>
            </Button>
          </li>
        );
      })}
    </ol>
  );
}
